import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    TextField,
    Button,
    InputAdornment,
    CircularProgress,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import EnumsService from '../../Services/enums.service.js';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const PalCodePrefixesDialog = ({
    open,
    onClose,
    caseId,
    accountantId,
    token,
    onSave,
}) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();

    const [searchQuery, setSearchQuery] = useState('');
    const [dataLoading, setDataLoading] = useState(false);
    const [dataError, setDataError] = useState(null);
    const [palCodesOptions, setPalCodesOptions] = useState([]);
    const [localPrefixValues, setLocalPrefixValues] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const inputRefs = useRef({});

    useEffect(() => {
        if (!open) {
            setSearchQuery('');
            setPage(0);
            return;
        }
        if (!caseId || !accountantId || !token) return;

        let cancelled = false;
        setDataError(null);
        setDataLoading(true);

        const load = async () => {
            try {
                const [categoriesResp, palCodesResp, prefixesResp] = await Promise.all([
                    EnumsService.GetPalCodeCategoriesByAccountantId(accountantId, token),
                    EnumsService.GetPalCodesByAccountantId(accountantId, token),
                    EnumsService.GetPalCodePrefixes(caseId, token),
                ]);
                if (cancelled) return;

                let palCodeCategoriesData = [];
                if (categoriesResp.ok) palCodeCategoriesData = await categoriesResp.json();

                const palCodeOrderMap = {};
                palCodeCategoriesData.forEach((cat) => {
                    palCodeOrderMap[cat.palCodeCategoryId] = cat.orderNumber ?? 9999;
                });

                let palCodesData = [];
                if (palCodesResp.ok) palCodesData = await palCodesResp.json();

                const sorted = (palCodesData || []).sort((a, b) => {
                    const orderA = palCodeOrderMap[a.palCodeId] ?? 9999;
                    const orderB = palCodeOrderMap[b.palCodeId] ?? 9999;
                    return orderA - orderB;
                });
                const options = sorted.map((p) => ({
                    key: p.palCodeId,
                    value: p.palCodeId,
                    label: p.palCodeId,
                }));
                setPalCodesOptions(options);

                let prefixes = [];
                if (prefixesResp.ok) {
                    const raw = await prefixesResp.json();
                    prefixes = Array.isArray(raw) ? raw : [];
                }

                const map = {};
                options.forEach((o) => { map[o.value] = ''; });
                prefixes.forEach((p) => { map[p.palCodeId] = p.prefix; });
                setLocalPrefixValues(map);
            } catch (err) {
                if (!cancelled) {
                    setDataError(err?.message || t('ErrorLoadingData') || 'Error loading data');
                    setPalCodesOptions([]);
                    setLocalPrefixValues({});
                }
            } finally {
                if (!cancelled) setDataLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [open, caseId, accountantId, token, t]);

    const filteredPalCodes = useMemo(() => {
        if (!searchQuery.trim()) return palCodesOptions;
        const q = searchQuery.toLowerCase().trim();
        return palCodesOptions.filter(
            (p) =>
                (p.label || '').toLowerCase().includes(q) ||
                (p.value || '').toLowerCase().includes(q) ||
                String(localPrefixValues[p.value] || '').includes(q)
        );
    }, [palCodesOptions, searchQuery, localPrefixValues]);

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredPalCodes.slice(start, start + rowsPerPage);
    }, [filteredPalCodes, page, rowsPerPage]);

    const assignedCount = useMemo(() => {
        return Object.values(localPrefixValues).filter(v => v !== '' && v != null).length;
    }, [localPrefixValues]);

    const handlePrefixChange = useCallback((palCodeId, value) => {
        setLocalPrefixValues((prev) => ({ ...prev, [palCodeId]: value }));
    }, []);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(0);
    };

    const handleClearFilter = () => {
        setSearchQuery('');
        setPage(0);
    };

    const minPrefix = 10; // 2 digits: 20 → 20001 – 20xxxxxxx
    const PREFIX_TARGET_LENGTH = 9;
    const PREFIX_MIN_SUFFIX_DIGITS = 3;

    const formatPrefixRange = (prefixNum) => {
        const prefixStr = String(Math.trunc(prefixNum));
        const suffixLen = Math.max(1, PREFIX_TARGET_LENGTH - prefixStr.length);
        const minId = `${prefixStr}${'0'.repeat(PREFIX_MIN_SUFFIX_DIGITS - 1)}1`;
        return `${minId} – ${prefixStr}${'x'.repeat(suffixLen)}`;
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir={dir}>
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" component="span">
                        {t('PalCodePrefixes') || 'Assign prefix to PalCodes'}
                    </Typography>
                    <Chip
                        size="small"
                        label={`${assignedCount} / ${palCodesOptions.length}`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: 11, height: 22 }}
                    />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pb: 0 }}>
                {dataLoading ? (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="textSecondary">{t('Loading') || 'Loading...'}</Typography>
                        {dataError && <Typography variant="body2" color="error">{dataError}</Typography>}
                    </Box>
                ) : (
                    <>
                        {/* Search */}
                        <Box sx={{ pt: 1, pb: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={t('SearchPalCode') || 'Search PAL code or prefix...'}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery ? (
                                        <InputAdornment position="end">
                                            <Button size="small" onClick={handleClearFilter} sx={{ minWidth: 0, p: 0.5 }}>
                                                <ClearIcon fontSize="small" />
                                            </Button>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            {searchQuery && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {filteredPalCodes.length} / {palCodesOptions.length} {t('Results') || 'results'}
                                </Typography>
                            )}
                        </Box>

                        {/* Table */}
                        <TableContainer sx={{ maxHeight: 400, overflowX: 'auto' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5', whiteSpace: 'nowrap' }}>
                                            {t('PalCode') || 'PalCode'}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5', whiteSpace: 'nowrap', width: 140 }}>
                                            {t('Prefix') || 'Prefix'}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5', whiteSpace: 'nowrap', width: 100 }}>
                                            {t('Range') || 'Range'}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.length > 0 ? paginatedRows.map((pal) => {
                                        const prefixVal = localPrefixValues[pal.value] ?? '';
                                        const numPrefix = Number(prefixVal);
                                        const hasValidPrefix = prefixVal !== '' && !isNaN(numPrefix) && numPrefix >= minPrefix;
                                        return (
                                            <TableRow key={pal.value} hover>
                                                <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, whiteSpace: 'nowrap' }}>
                                                    {pal.label}
                                                </TableCell>
                                                <TableCell sx={{ py: 0.5, px: 1 }}>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        placeholder={t('PrefixPlaceholder') || 'e.g. 20'}
                                                        inputProps={{ min: minPrefix, style: { fontSize: 12, padding: '4px 8px' } }}
                                                        inputRef={(el) => { inputRefs.current[pal.value] = el; }}
                                                        value={prefixVal}
                                                        onChange={(e) => handlePrefixChange(pal.value, e.target.value)}
                                                        sx={{ width: 110, '& .MuiOutlinedInput-root': { height: 30 } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 0.5, px: 1, fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                    {hasValidPrefix
                                                        ? formatPrefixRange(numPrefix)
                                                        : '—'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }) : (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ py: 3, textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>
                                                {searchQuery
                                                    ? (t('NoMatchingPalCodes') || 'No matching PAL codes.')
                                                    : (t('NoPalCodes') || 'No PalCodes available.')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <TablePagination
                            component="div"
                            count={filteredPalCodes.length}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                            sx={{ borderTop: '1px solid #e0e0e0', '& .MuiTablePagination-toolbar': { minHeight: 40 } }}
                            labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                        />
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={onClose}>{t('cancelButton') || 'Cancel'}</Button>
                <Button variant="contained" onClick={() => onSave(localPrefixValues)} disabled={dataLoading}>
                    {t('Save') || 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PalCodePrefixesDialog;
